import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box } from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import type { ColDef, ICellRendererParams } from "ag-grid-community";
import { listNdas, updateNda } from "../../services/ndasApi";
import { useReferenceLists } from "../../context/GlobalGetLists";
import { CustomButton } from "../../components/common/customButton";
import { areRecordsEqual, getChangedRecords } from "../../utils/compareData";
import { CustomTextInput } from "../../components/common/inputFields/customTextInput";
import { SimpleToolbar } from "../../components/layout/Toolbar";
import { CustomIconButton } from "../../components/common/customIconButton";
import { MUISelectCellEditor } from "../../components/common/gridSelectCell";
import { NonCircumventBadge } from "../../components/deal/NonCircumventBadge";
import { createSingleSelectColumn } from "../../utils/columnHelpers";
import {
  formatAppliesTo,
  NDA_APPLIES_TO_OPTIONS,
  type Nda,
  type NdaAppliesTo,
  type NdaRestrictionType,
} from "../../types/nda";
import { formatRestrictionType, isNdaExpired } from "../../utils/nonCircumvent";
import { useCurrentUser } from "@/context/CurrentUserContext";
import { useErrorDialog } from "../../hooks/useErrorDialog";
import { ErrorDialog } from "../../components/common/errorDialog";
import { CustomSingleSelect } from "../../components/common/inputFields/customSingleSelect";
import pencilIcon from "@/assets/svg/edit.svg";
import downloadIcon from "@/assets/svg/downloads-dark.svg";
import "../../assets/css/Text-General.css";
import "../../assets/css/AGGrid-custom.css";
import { useGridEditCommit } from "@/hooks/useGridEditCommit";

const RESTRICTION_TYPE_OPTIONS: { id: NdaRestrictionType; label: string }[] = [
  { id: "non_circumvent", label: "Non-circumvent" },
  { id: "no_contact", label: "No contact" },
];

const ACTIVITY_FILTER_OPTIONS = [
  { id: "all", name: "All" },
  { id: "active", name: "Active" },
  { id: "inactive", name: "Inactive" },
] as const;

type ActivityFilter = (typeof ACTIVITY_FILTER_OPTIONS)[number]["id"];

function NdaStatusPillRenderer(params: ICellRendererParams<Nda>) {
  if (!params.data) return null;
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        height: "100%",
        py: 0.25,
      }}
    >
      <NonCircumventBadge nda={params.data} compact />
    </Box>
  );
}

function normalizeNdaRow(nda: Nda): Nda {
  const person =
    nda.person_responsible && typeof nda.person_responsible === "object"
      ? nda.person_responsible
      : typeof nda.person_responsible_id === "number"
        ? { id: nda.person_responsible_id, name: "" }
        : null;

  return {
    ...nda,
    expiry_date: nda.expiry_date
      ? String(nda.expiry_date).slice(0, 10)
      : null,
    person_responsible: person,
    person_responsible_id:
      person && typeof person.id === "number" ? person.id : null,
  };
}

function toWritePayload(nda: Nda) {
  const person =
    nda.person_responsible && typeof nda.person_responsible === "object"
      ? nda.person_responsible
      : null;
  const personId =
    person && typeof person.id === "number"
      ? person.id
      : typeof nda.person_responsible_id === "number"
        ? nda.person_responsible_id
        : null;

  return {
    restriction_type: (nda.restriction_type || null) as
      | NdaRestrictionType
      | null,
    applies_to: (nda.applies_to || null) as NdaAppliesTo | null,
    summary: nda.summary?.trim() ? nda.summary.trim() : null,
    expiry_date: nda.expiry_date
      ? String(nda.expiry_date).slice(0, 10)
      : null,
    person_responsible_id: personId,
    person_responsible: personId != null ? { id: personId } : null,
  };
}

const Ndas = () => {
  const { canEdit } = useCurrentUser();
  const { users } = useReferenceLists();
  const gridRef = useRef<AgGridReact>(null);
  const { errorDialog, showErrorFromResponse, hideError } = useErrorDialog();

  const [editMode, setEditMode] = useState(false);
  const [filter, setFilter] = useState("");
  const [activityFilter, setActivityFilter] = useState<ActivityFilter>("all");
  const [dirtyGrid, setDirtyGrid] = useState(false);
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<Nda[]>([]);
  const [initialRows, setInitialRows] = useState<Nda[]>([]);

  const linkedDeals = useCallback((nda: Nda | null | undefined) => {
    if (!Array.isArray(nda?.deals)) return [];
    return nda.deals.filter(
      (d): d is { id: number; deal_name?: string | null } =>
        d != null && typeof d.id === "number",
    );
  }, []);

  const linkedDealNames = useCallback(
    (nda: Nda | null | undefined) =>
      linkedDeals(nda)
        .map((d) =>
          typeof d.deal_name === "string" && d.deal_name.trim()
            ? d.deal_name.trim()
            : "",
        )
        .filter(Boolean)
        .join(", "),
    [linkedDeals],
  );

  const loadNdas = useCallback(async () => {
    setLoading(true);
    try {
      const response = await listNdas();
      if (!response.success) {
        showErrorFromResponse(response);
        setRows([]);
        setInitialRows([]);
        return;
      }
      const next = (response.data || []).map(normalizeNdaRow);
      setRows(next);
      setInitialRows(structuredClone(next));
    } finally {
      setLoading(false);
    }
  }, [showErrorFromResponse]);

  useEffect(() => {
    void loadNdas();
  }, [loadNdas]);

  useEffect(() => {
    setDirtyGrid(!areRecordsEqual(initialRows, rows));
  }, [initialRows, rows]);

  useEffect(() => {
    if (gridRef.current && editMode) {
      setTimeout(() => {
        gridRef.current?.api.refreshCells({ force: true });
      }, 0);
    }
  }, [editMode]);

  const onCellValueChanged = useCallback((event: any) => {
    const { newValue, oldValue, data } = event;
    if (newValue === oldValue) return;

    setRows((prev) =>
      prev.map((row) => {
        if (row.id !== data.id) return row;
        const next = { ...data } as Nda;
        if (
          next.person_responsible &&
          typeof next.person_responsible === "object" &&
          typeof next.person_responsible.id === "number"
        ) {
          next.person_responsible_id = next.person_responsible.id;
        } else if (next.person_responsible == null) {
          next.person_responsible_id = null;
        }
        return next;
      }),
    );
  }, []);

  const handleCancelButtonPress = () => {
    setEditMode(false);
    setRows(structuredClone(initialRows));
    setDirtyGrid(false);
  };

  const handleSaveButtonPress = async () => {
    const changedItems = getChangedRecords(rows, initialRows).filter(
      (nda): nda is Nda & { id: number } => typeof nda.id === "number",
    );

    if (changedItems.length === 0) {
      setEditMode(false);
      return;
    }

    const results = await Promise.all(
      changedItems.map((nda) =>
        updateNda(nda.id, toWritePayload(nda)).catch((error) => {
          console.error(`Failed to update NDA ${nda.id}`, error);
          return { success: false as const };
        }),
      ),
    );

    const failed = results.filter((r) => !r?.success);
    if (failed.length > 0) {
      window.alert(
        `Failed to update ${failed.length} NDA(s). Check console for details.`,
      );
      return;
    }

    setEditMode(false);
    await loadNdas();
  };

  const { isCellEditing, requestSave, gridEditProps } =
    useGridEditCommit(handleSaveButtonPress);

  const usersList = Array.isArray(users) ? users : [];

  const filteredRows = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return rows.filter((nda) => {
      const expired = isNdaExpired(nda.expiry_date);
      if (activityFilter === "active" && expired) return false;
      if (activityFilter === "inactive" && !expired) return false;

      if (!q) return true;
      const personName =
        nda.person_responsible && typeof nda.person_responsible === "object"
          ? nda.person_responsible.name
          : "";
      const haystack = [
        formatRestrictionType(nda.restriction_type),
        formatAppliesTo(nda.applies_to),
        nda.summary,
        nda.expiry_date,
        personName,
        linkedDealNames(nda),
        nda.id != null ? String(nda.id) : "",
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [rows, filter, activityFilter, linkedDealNames]);

  const columnDefs = useMemo<ColDef<Nda>[]>(
    () => [
      {
        headerName: "Deal",
        colId: "deals",
        width: 260,
        editable: false,
        valueGetter: (params) => linkedDealNames(params.data),
        cellRenderer: (params: ICellRendererParams<Nda>) => {
          const deals = linkedDeals(params.data);
          if (deals.length === 0) return null;
          return (
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 0.75,
                alignItems: "center",
                py: 0.25,
              }}
            >
              {deals.map((deal, index) => {
                const name =
                  typeof deal.deal_name === "string" && deal.deal_name.trim()
                    ? deal.deal_name.trim()
                    : `Deal ${deal.id}`;
                return (
                  <Box
                    key={deal.id}
                    component="span"
                    sx={{ display: "inline-flex", alignItems: "center" }}
                  >
                    <a
                      href={`/deals/${deal.id}`}
                      style={{ color: "#1B7A63", textDecoration: "underline" }}
                    >
                      {name}
                    </a>
                    {index < deals.length - 1 ? "," : null}
                  </Box>
                );
              })}
            </Box>
          );
        },
      },
      {
        headerName: "Status",
        colId: "status_pill",
        width: 90,
        editable: false,
        sortable: false,
        filter: false,
        cellRenderer: NdaStatusPillRenderer,
        valueGetter: (params) => params.data?.expiry_date ?? "",
      },
      {
        field: "restriction_type",
        headerName: "Restriction type",
        editable: editMode,
        width: 170,
        cellEditor: MUISelectCellEditor,
        cellEditorParams: {
          values: RESTRICTION_TYPE_OPTIONS,
          label: "Restriction type",
          multiple: false,
          squareSelectCorners: true,
        },
        valueGetter: (params) => {
          const v = params.data?.restriction_type;
          if (v == null || v === "") return null;
          const match = RESTRICTION_TYPE_OPTIONS.find((o) => o.id === v);
          return match || { id: v, label: formatRestrictionType(v) };
        },
        valueSetter: (params) => {
          if (!params.data) return false;
          const nv = params.newValue;
          const id =
            nv == null
              ? null
              : typeof nv === "object"
                ? (nv.id ?? null)
                : nv;
          params.data.restriction_type =
            id === "no_contact" || id === "non_circumvent" ? id : null;
          return true;
        },
        valueFormatter: ({ value }) => {
          if (value == null) return "";
          if (typeof value === "object") return value.label || value.id || "";
          return formatRestrictionType(String(value));
        },
      },
      {
        field: "expiry_date",
        headerName: "Expiry",
        editable: editMode,
        width: 130,
        valueFormatter: ({ value }) =>
          value ? String(value).slice(0, 10) : "",
      },
      {
        field: "applies_to",
        headerName: "Applies to",
        editable: editMode,
        width: 180,
        cellEditor: MUISelectCellEditor,
        cellEditorParams: {
          values: [
            { id: null, label: "(None)" },
            ...NDA_APPLIES_TO_OPTIONS.map((o) => ({
              id: o.id,
              label: o.name,
            })),
          ],
          label: "Applies to",
          multiple: false,
          squareSelectCorners: true,
        },
        valueGetter: (params) => {
          const v = params.data?.applies_to;
          if (v == null || v === "") return { id: null, label: "(None)" };
          const match = NDA_APPLIES_TO_OPTIONS.find((o) => o.id === v);
          return match
            ? { id: match.id, label: match.name }
            : { id: v, label: formatAppliesTo(v) };
        },
        valueSetter: (params) => {
          if (!params.data) return false;
          const nv = params.newValue;
          const id =
            nv == null
              ? null
              : typeof nv === "object"
                ? (nv.id ?? null)
                : nv;
          params.data.applies_to =
            id == null || id === "" ? null : (String(id) as NdaAppliesTo);
          return true;
        },
        valueFormatter: ({ value }) => {
          if (value == null) return "";
          if (typeof value === "object") {
            if (value.id == null) return "";
            return value.label || formatAppliesTo(value.id);
          }
          return formatAppliesTo(String(value));
        },
      },
      {
        ...(createSingleSelectColumn(
          "person_responsible",
          "Person responsible",
          usersList,
          "name",
          180,
          editMode,
          true,
        ) as ColDef<Nda>),
      },
      {
        field: "summary",
        headerName: "Summary",
        editable: editMode,
        flex: 1,
        minWidth: 280,
        wrapText: true,
        autoHeight: true,
        cellEditor: "agLargeTextCellEditor",
        cellEditorPopup: true,
        cellEditorParams: {
          maxLength: 10000,
          rows: 10,
          cols: 60,
        },
        cellStyle: {
          whiteSpace: "pre-wrap",
          lineHeight: "1.35",
          paddingTop: 6,
          paddingBottom: 6,
        },
      },
    ],
    [editMode, usersList, linkedDeals, linkedDealNames],
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
        padding: 0,
        boxSizing: "border-box",
        overflow: "hidden",
      }}
    >
      <Box sx={{ flexShrink: 0 }}>
        <SimpleToolbar>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "15px",
              minWidth: 0,
            }}
          >
            <CustomTextInput
              label="Search NDAs"
              value={filter}
              onChange={(val) => typeof val === "string" && setFilter(val)}
            />
            <CustomSingleSelect
              value={activityFilter}
              onChange={(e) => {
                const next = String(e.target.value || "all") as ActivityFilter;
                setActivityFilter(
                  next === "active" || next === "inactive" ? next : "all",
                );
              }}
              options={[...ACTIVITY_FILTER_OPTIONS]}
              labelKey="name"
              placeholder="Active/Inactive"
              minWidth="140px"
            />
          </Box>
          {editMode ? (
            <Box
              sx={{ display: "flex", alignItems: "center", gap: "15px" }}
              data-position="right"
            >
              <CustomButton
                text="Cancel"
                handleButtonPress={handleCancelButtonPress}
                isButtonDisabled={false}
                solid={false}
                widthOverride="100px"
              />
              <CustomButton
                text="Save"
                handleButtonPress={requestSave}
                isButtonDisabled={!dirtyGrid && !isCellEditing}
                solid
                widthOverride="100px"
              />
            </Box>
          ) : (
            <Box
              data-position="right"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.25,
                ml: -0.5,
                "& .MuiIconButton-root": {
                  p: 0.6,
                },
                "& .MuiIconButton-root .MuiSvgIcon-root": {
                  fontSize: 22,
                },
              }}
            >
              <CustomIconButton
                tooltip="Download"
                icon={
                  <Box
                    component="img"
                    src={downloadIcon}
                    alt="Download NDAs"
                    sx={{ width: 30, height: 30, display: "block" }}
                  />
                }
                iconButtonSx={{ p: 0 }}
                onClick={() =>
                  gridRef.current?.api.exportDataAsCsv({ fileName: "ndas" })
                }
              />
              <CustomIconButton
                tooltip="Edit"
                icon={
                  <Box
                    component="img"
                    src={pencilIcon}
                    alt="Edit NDAs"
                    sx={{ width: 20, height: 20, display: "block" }}
                  />
                }
                disabled={!canEdit || loading}
                onClick={() => {
                  if (!canEdit) return;
                  setEditMode(true);
                }}
              />
            </Box>
          )}
        </SimpleToolbar>
      </Box>

      <Box sx={{ flexGrow: 1, minHeight: 0, p: 0 }}>
        <div
          className="ag-theme-alpine"
          style={{ width: "100%", height: "100%" }}
        >
          <AgGridReact
            {...gridEditProps}
            ref={gridRef}
            rowData={filteredRows}
            columnDefs={columnDefs}
            onCellValueChanged={onCellValueChanged}
            singleClickEdit
            defaultColDef={{ resizable: true }}
            getRowId={({ data }) => data?.id?.toString() ?? ""}
            overlayNoRowsTemplate={
              loading ? "Loading NDAs..." : "No NDAs found"
            }
          />
        </div>
      </Box>

      <ErrorDialog
        open={errorDialog.open}
        onClose={hideError}
        apiResponse={errorDialog.apiResponse}
        customMessage={errorDialog.customMessage}
        title={errorDialog.title}
      />
    </Box>
  );
};

export default Ndas;
